"use client";

import { useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";
import type { CarbonToken } from "@/types/glassroom";
import {
  Paper,
  Text,
  DetailRow,
  TbQrcode,
  TbCertificate,
  TbCalendar,
  TbMapPin,
  TbId,
  TbTarget,
  TbNote,
  TbCoins,
  Anchor,
} from "@forestsync/ui";
import { TbLeaf, TbHomeEco, TbEmpathize } from "@forestsync/ui";
import { TOKEN_STATUS, CREDIT_TYPE_LABEL } from "@forestsync/db";
import { TOKEN_STATUS_TW_CLASS, TOKEN_STATUS_GLASS_BG } from "@forestsync/lib/type-utils";
import { getTokenStatusDisplayName } from "@forestsync/lib/type-utils";
import { QRCode } from "@/components/glassroom/common";
import { HyperLink } from "@/components/glassroom/common/HyperLink";

export function PassportView({
  token,
  showSerialInTitle = true,
}: {
  token: CarbonToken;
  showSerialInTitle?: boolean;
}) {
  const t = useTranslations();
  const twClass = TOKEN_STATUS_TW_CLASS[token.status];
  const glassClass = TOKEN_STATUS_GLASS_BG[token.status];
  const [currentUrl, setCurrentUrl] = useState<string>("");

  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  // Calculate expiration date based on project type and status
  const getExpirationDate = () => {
    if (token.status === TOKEN_STATUS.RETIRED && token.retirementDate) {
      // For retired tokens, expiration is lifespan from retirement date
      const retirementDate = new Date(token.retirementDate);
      retirementDate.setFullYear(retirementDate.getFullYear() + token.lifespan);
      return retirementDate;
    } else if ((token.projectType === 1 || token.projectType === 0) && token.validationDate) {
      // ex-ante (creditClass=1) or unknown: expiration is lifespan from validation date
      const validationDate = new Date(token.validationDate);
      validationDate.setFullYear(validationDate.getFullYear() + token.lifespan);
      return validationDate;
    }
    // ex-post (creditClass=2): no expiration until retired
    return null;
  };

  const expirationDate = getExpirationDate();

  // Remove theme/isDark/statusColor logic, only use className for badge color
  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Left: Serial Number and QR Code */}
        <div className="flex flex-col gap-4 md:col-span-5">
          <Paper
            withBorder
            radius="lg"
            p="md"
            className={`flex flex-col shadow-md h-full min-h-[200px] ${glassClass}`}
          >
            <div className="mb-4">
              <Text size="lg" fw={700} className="mb-2">
                {t("passport.title")}
              </Text>
              <Text
                size="xl"
                className="font-mono text-gray-700 dark:text-gray-300 break-all"
              >
                {token.serialNumber}
              </Text>
            </div>
            <div className="flex-1 flex items-center justify-center">
              {currentUrl ? (
                <QRCode
                  key={currentUrl}
                  value={currentUrl}
                  size={200}
                  className="mx-auto"
                />
              ) : (
                <TbQrcode
                  size={64}
                  className="text-blue-400 dark:text-blue-300"
                />
              )}
            </div>
          </Paper>
        </div>
        {/* Right: Summary and Details */}
        <div className="flex flex-col gap-4 md:col-span-7">
          {/* Token Summary Card */}
          <Paper withBorder radius="lg" p="md" className="shadow-md">
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {/* Status */}
              <DetailRow
                icon={TbCertificate}
                label={t("common.status")}
                value={
                  <span
                    className={`px-3 py-1 text-base font-medium rounded-full ${twClass}`}
                  >
                    {getTokenStatusDisplayName(token.status, t)}
                  </span>
                }
                className="py-2"
              />
              {/* Serial Number (if not in title) */}
              {!showSerialInTitle && (
                <DetailRow
                  icon={TbId}
                  label={t("passport.serialNumber")}
                  value={
                    <span className="font-mono">{token.serialNumber}</span>
                  }
                  className="py-2"
                />
              )}
              {/* Credit Lifespan */}
              <DetailRow
                icon={TbTarget}
                label={t("sink.lifespan")}
                value={t("passport.creditTypeYears", {
                  years: token.lifespan,
                })}
                className="py-2"
              />
              {/* Current Owner */}
              <DetailRow
                icon={token.isPDOwned ? TbHomeEco : TbEmpathize}
                label={t("passport.currentOwner")}
                value={
                  <HyperLink
                    type={token.isPDOwned ? "pd" : "b"}
                    refId={token.currentOwnerRefId}
                  >
                    {token.ownerName}
                  </HyperLink>
                }
                className="py-2"
              />
              {/* Validation Date */}
              <DetailRow
                icon={TbCalendar}
                label={t("passport.validationDate")}
                value={
                  token.validationDate
                    ? new Date(token.validationDate).toLocaleDateString()
                    : "-"
                }
                className="py-2"
              />
              {/* Retirement Date */}
              {token.status === TOKEN_STATUS.RETIRED &&
                token.retirementDate && (
                  <DetailRow
                    icon={TbCalendar}
                    label={t("passport.retirementDate")}
                    value={new Date(token.retirementDate).toLocaleDateString()}
                    className="py-2"
                  />
                )}
              {/* Expiration Date */}
              <DetailRow
                icon={TbCalendar}
                label={t("passport.expirationDate")}
                value={
                  expirationDate ? expirationDate.toLocaleDateString() : "-"
                }
                className="py-2"
              />
            </div>
          </Paper>
          {/* Credit Details Card */}
          <Paper withBorder radius="lg" p="md" className="shadow-md">
            <Text fw={700} size="md" className="mb-2">
              {t("passport.forestDetails")}
            </Text>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {/* Project Type */}
              <DetailRow
                icon={TbTarget}
                label={t("passport.projectType")}
                value={CREDIT_TYPE_LABEL[token.creditType as keyof typeof CREDIT_TYPE_LABEL] ?? token.creditType ?? "N/A"}
                className="py-2"
              />
              {/* Forest Name */}
              <DetailRow
                icon={TbLeaf}
                label={t("passport.forestName")}
                value={
                  <HyperLink type="sink" refId={token.sinkRefId}>
                    {token.sinkName}
                  </HyperLink>
                }
                className="py-2"
              />
              {/* Forest Location */}
              <DetailRow
                icon={TbMapPin}
                label={t("passport.forestLocation")}
                value={[
                  token.sinkCountry,
                  token.sinkRegion,
                  token.sinkMunicipality,
                ]
                  .filter(Boolean)
                  .join(", ")}
                className="py-2"
              />
              {/* Project Developer */}
              <DetailRow
                icon={TbHomeEco}
                label={t("passport.projectDeveloper")}
                value={
                  <HyperLink type="pd" refId={token.projectDeveloperRefId}>
                    {token.projectDeveloperName}
                  </HyperLink>
                }
                className="py-2"
              />
            </div>
          </Paper>
          {/* External Registry References */}
          {token.externalRegistries && token.externalRegistries.length > 0 && (
            <Paper withBorder radius="lg" p="md" className="shadow-md">
              <Text fw={700} size="md" className="mb-2">
                {t("passport.externalRegistries")}
              </Text>
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {token.externalRegistries.map((ref, i) => (
                  <DetailRow
                    key={i}
                    icon={TbCertificate}
                    label={ref.label ?? ref.registry}
                    value={
                      ref.url ? (
                        <Anchor href={ref.url} target="_blank" rel="noopener noreferrer" size="sm">
                          {ref.projectId}
                        </Anchor>
                      ) : (
                        ref.projectId
                      )
                    }
                    className="py-2"
                  />
                ))}
              </div>
            </Paper>
          )}
          {/* Retirement Details */}
          {token.status === TOKEN_STATUS.RETIRED && (            <Paper withBorder radius="lg" p="md" className="shadow-md">
              <Text fw={700} size="md" className="mb-2">
                {t("passport.retirementDetails")}
              </Text>
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                <DetailRow
                  icon={TbCoins}
                  label={t("passport.retirementPrice")}
                  value={
                    token.retirementPrice
                      ? `€${token.retirementPrice.toFixed(2)}`
                      : t("common.notSpecified")
                  }
                  className="py-2"
                />
                <DetailRow
                  icon={TbNote}
                  label={t("passport.retirementReason")}
                  value={token.retirementReason || t("common.notSpecified")}
                  className="py-2"
                />
              </div>
            </Paper>
          )}
          {/* Destroyed/Replacement Info */}
          {token.status === TOKEN_STATUS.REVERSED && (
            <Paper
              withBorder
              radius="lg"
              p="md"
              className="shadow-md bg-red-50 dark:bg-red-900/20"
            >
              <Text size="sm" c="red" fw={700}>
                {token.replacementSerial
                  ? t("passport.replacedBy", {
                      serial: token.replacementSerial,
                    })
                  : t("passport.waitingForReplacement")}
              </Text>
            </Paper>
          )}
        </div>
      </div>
    </div>
  );
}
